const FALLBACK_LOCALE = 'en-US';

function getLocale(): string {
  if (typeof navigator === 'undefined') {
    return FALLBACK_LOCALE;
  }

  const candidateLocales = [...(navigator.languages ?? []), navigator.language].filter(
    (locale): locale is string => Boolean(locale),
  );
  const [supportedLocale] = Intl.DateTimeFormat.supportedLocalesOf(candidateLocales);

  return supportedLocale || navigator.language || FALLBACK_LOCALE;
}

type DateTimeFormatters = {
  dateShortWithYear: Intl.DateTimeFormat;
  dateShortWithDay: Intl.DateTimeFormat;
  dateLong: Intl.DateTimeFormat;
  timeShort: Intl.DateTimeFormat;
  timeLong: Intl.DateTimeFormat;
  dateTimeShort: Intl.DateTimeFormat;
  dateTimeLong: Intl.DateTimeFormat;
};

const dateTimeFormattersByLocale = new Map<string, DateTimeFormatters>();

function createFormatter(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(locale, {
    ...options,
    // Keep locale-specific field ordering, but always use Gregorian dates and Latin digits.
    calendar: 'gregory',
    numberingSystem: 'latn',
  } as Intl.DateTimeFormatOptions);
}

function createDateTimeFormatters(locale: string): DateTimeFormatters {
  return {
    dateShortWithYear: createFormatter(locale, {
      year: 'numeric',
      month: '2-digit',
      hour12: false,
    }),
    dateShortWithDay: createFormatter(locale, {
      month: 'short',
      day: '2-digit',
      hour12: false,
    }),
    dateLong: createFormatter(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour12: false,
    }),
    timeShort: createFormatter(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }),
    timeLong: createFormatter(locale, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }),
    dateTimeShort: createFormatter(locale, {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }),
    dateTimeLong: createFormatter(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }),
  };
}

// This function allows creating new formatters in case locale changes, which is helpful when mocking locale in tests
function getDateTimeFormatters(): DateTimeFormatters {
  const locale = getLocale();
  let dateTimeFormatters = dateTimeFormattersByLocale.get(locale);

  if (!dateTimeFormatters) {
    dateTimeFormatters = createDateTimeFormatters(locale);
    dateTimeFormattersByLocale.set(locale, dateTimeFormatters);
  }

  return dateTimeFormatters;
}

export function formatDateShortWithYear(date: Date | number): string {
  const dateTimeFormatters = getDateTimeFormatters();
  return dateTimeFormatters.dateShortWithYear.format(new Date(date));
}

export function formatDateShortWithDay(date: Date | number): string {
  const dateTimeFormatters = getDateTimeFormatters();
  return dateTimeFormatters.dateShortWithDay.format(new Date(date));
}

export function formatDateLong(date: Date | number): string {
  const dateTimeFormatters = getDateTimeFormatters();
  return dateTimeFormatters.dateLong.format(new Date(date));
}

export function formatTimeShort(date: Date | number): string {
  const dateTimeFormatters = getDateTimeFormatters();
  return dateTimeFormatters.timeShort.format(new Date(date));
}

export function formatTimeLong(date: Date | number): string {
  const dateTimeFormatters = getDateTimeFormatters();
  return dateTimeFormatters.timeLong.format(new Date(date));
}

export function formatDateTimeShort(date: Date | number): string {
  const dateTimeFormatters = getDateTimeFormatters();
  return dateTimeFormatters.dateTimeShort.format(new Date(date));
}

export function formatDateTimeLong(date: Date | number): string {
  const dateTimeFormatters = getDateTimeFormatters();
  return dateTimeFormatters.dateTimeLong.format(new Date(date));
}
